import { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Sparkles, Loader2, TrendingUp, Download } from "lucide-react";
import { toast } from "sonner";

export default function Reports() {
  const [sales, setSales] = useState(null);
  const [pl, setPl] = useState(null);
  const [inv, setInv] = useState(null);
  const [forecast, setForecast] = useState("");
  const [genLoading, setGenLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/reports/sales").then((r) => setSales(r.data)),
      api.get("/reports/profit-loss").then((r) => setPl(r.data)),
      api.get("/reports/inventory").then((r) => setInv(r.data)),
    ]);
  }, []);

  const generateForecast = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post("/ai/sales-forecast", { horizon_days: 30 });
      setForecast(data.forecast);
    } catch {
      toast.error("AI forecast failed");
    }
    setGenLoading(false);
  };

  const csvExport = (rows, name) => {
    if (!rows || rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => JSON.stringify(r[h] ?? "")).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${name}.csv`; a.click();
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Reports</h1>
        <p className="text-sm text-muted-foreground mt-1">Sales, P&L, Inventory, GST · with AI forecasting</p>
      </div>

      <Tabs defaultValue="pl">
        <TabsList>
          <TabsTrigger value="pl" data-testid="tab-pl">Profit & Loss</TabsTrigger>
          <TabsTrigger value="sales" data-testid="tab-sales-report">Sales</TabsTrigger>
          <TabsTrigger value="inv" data-testid="tab-inv-report">Inventory</TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai-forecast">AI Forecast</TabsTrigger>
        </TabsList>

        <TabsContent value="pl" className="mt-4">
          {pl && (
            <div className="grid sm:grid-cols-4 gap-4">
              <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground">Revenue</div><div className="font-heading font-bold text-2xl mt-1">{inr(pl.revenue)}</div></Card>
              <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground">Expenses</div><div className="font-heading font-bold text-2xl mt-1 text-rose-600">{inr(pl.expenses)}</div></Card>
              <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground">Profit</div><div className="font-heading font-bold text-2xl mt-1 text-emerald-600">{inr(pl.profit)}</div></Card>
              <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground">Margin</div><div className="font-heading font-bold text-2xl mt-1">{pl.margin_percent}%</div></Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="sales" className="mt-4">
          {sales && (
            <>
              <div className="flex justify-between items-end mb-4">
                <div className="text-sm text-muted-foreground">{sales.count} invoices · Total {inr(sales.total)}</div>
                <Button variant="outline" size="sm" onClick={() => csvExport(sales.invoices, "sales")} data-testid="export-sales-csv"><Download size={14} className="mr-1" /> Export CSV</Button>
              </div>
              <Card className="overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice</TableHead><TableHead>Customer</TableHead><TableHead>Date</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sales.invoices.slice(0, 50).map((i) => (
                      <TableRow key={i.id}>
                        <TableCell className="font-medium">{i.invoice_number}</TableCell>
                        <TableCell>{i.customer_name}</TableCell>
                        <TableCell>{(i.invoice_date || "").slice(0, 10)}</TableCell>
                        <TableCell>{inr(i.grand_total)}</TableCell>
                        <TableCell>{i.status}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="inv" className="mt-4">
          {inv && (
            <>
              <div className="grid sm:grid-cols-3 gap-4 mb-4">
                <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground">Products</div><div className="font-heading font-bold text-2xl mt-1">{inv.total_products}</div></Card>
                <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground">Value</div><div className="font-heading font-bold text-2xl mt-1">{inr(inv.total_value)}</div></Card>
                <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground">Low Stock</div><div className="font-heading font-bold text-2xl mt-1 text-orange-600">{inv.low_stock_count}</div></Card>
              </div>
              <Button variant="outline" size="sm" onClick={() => csvExport(inv.products, "inventory")}><Download size={14} className="mr-1" /> Export CSV</Button>
            </>
          )}
        </TabsContent>

        <TabsContent value="ai" className="mt-4">
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Sparkles size={18} /></div>
              <div>
                <div className="font-heading font-bold">AI Sales Forecast</div>
                <div className="text-xs text-muted-foreground">Predict next 30 days using Gemini 3 Flash</div>
              </div>
              <Button onClick={generateForecast} disabled={genLoading} className="ml-auto" data-testid="generate-forecast">
                {genLoading ? <Loader2 size={14} className="animate-spin mr-1" /> : <TrendingUp size={14} className="mr-1" />}
                Generate Forecast
              </Button>
            </div>
            {forecast ? (
              <div className="prose prose-sm max-w-none dark:prose-invert whitespace-pre-wrap bg-muted/30 rounded-lg p-4 text-sm" data-testid="forecast-result">{forecast}</div>
            ) : (
              <div className="text-sm text-muted-foreground p-6 text-center bg-muted/30 rounded-lg">Click &quot;Generate Forecast&quot; to get AI-powered sales projections and insights.</div>
            )}
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
